import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Facebook, Clock } from 'lucide-react';

const BOOKING_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_6b6596b0-1b37-4378-8699-89c151293ef2/artifacts/psnsg0g1_FAVICON%20AH.png";

const quickLinks = [
  { name: 'Hair Services', path: '/hair' },
  { name: 'Beauty Services', path: '/beauty' },
  { name: 'Wellness Services', path: '/wellness' },
  { name: 'Sustainability', path: '/sustainability' },
  { name: 'Book Online', path: BOOKING_URL, external: true },
];

const hours = [
  { day: 'Monday', time: 'Closed' },
  { day: 'Tuesday', time: '9:00 AM - 5:30 PM' },
  { day: 'Wednesday', time: '9:00 AM - 5:30 PM' },
  { day: 'Thursday', time: '9:00 AM - 8:00 PM' },
  { day: 'Friday', time: '9:00 AM - 5:30 PM' },
  { day: 'Saturday', time: '9:00 AM - 5:00 PM' },
  { day: 'Sunday', time: 'Closed' },
];

export default function Footer() {
  return (
    <footer className="bg-[#2C2420] text-white" data-testid="main-footer">
      {/* Main Footer */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img 
                src={LOGO_URL} 
                alt="Artistry House" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h3 className="text-xl font-medium">Artistry House</h3>
                <p className="text-xs text-[#BCAAA4] tracking-wide">Hair · Beauty · Wellness</p>
              </div>
            </Link>
            <p className="text-[#BCAAA4] text-sm leading-relaxed mb-6">
              Established in 1978 in Camberwell, we are dedicated to providing first-class hair, beauty, and wellness services in a luxurious environment.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/artistryhouse_hair/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#3D352D] flex items-center justify-center hover:bg-[#8D7B68] transition-colors"
                data-testid="social-instagram-hair"
                title="Hair Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/artistryhouse_beauty/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#3D352D] flex items-center justify-center hover:bg-[#8D7B68] transition-colors"
                data-testid="social-instagram-beauty"
                title="Beauty Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.facebook.com/people/Artistry-House/61587846745152/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#3D352D] flex items-center justify-center hover:bg-[#8D7B68] transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-medium mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  {link.external ? (
                    <a 
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer-link text-[#BCAAA4] hover:text-[#D4C4B0] text-sm"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link 
                      to={link.path}
                      className="footer-link text-[#BCAAA4] hover:text-[#D4C4B0] text-sm"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="text-lg font-medium mb-6 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#D4C4B0]" />
              Opening Hours
            </h4>
            <ul className="space-y-2">
              {hours.map((item) => (
                <li key={item.day} className="flex justify-between text-sm">
                  <span className="text-[#BCAAA4]">{item.day}</span>
                  <span className={item.time === 'Closed' ? 'text-[#6B5D4D]' : 'text-white'}>
                    {item.time}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-medium mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="https://maps.google.com/?q=925+Burke+Rd+Camberwell+VIC+3124"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-[#BCAAA4] hover:text-[#D4C4B0] transition-colors"
                >
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">925 Burke Rd,<br />Camberwell VIC 3124</span>
                </a>
              </li>
              <li>
                <a 
                  href="tel:0398826969"
                  className="flex items-center gap-3 text-[#BCAAA4] hover:text-[#D4C4B0] transition-colors"
                >
                  <Phone className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">(03) 9882 6969</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:hello@artistryhousehbw.com"
                  className="flex items-center gap-3 text-[#BCAAA4] hover:text-[#D4C4B0] transition-colors"
                >
                  <Mail className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">hello@artistryhousehbw.com</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#3D352D]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-[#6B5D4D]">
            <p>© {new Date().getFullYear()} Artistry House of Hair, Beauty & Wellness. All rights reserved.</p>
            <div className="flex items-center gap-2">
              <span>Proudly a</span>
              <span className="text-[#D4C4B0] font-medium">Sustainable Salon</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
